import { useState } from "react";
import { ListItem, Grid, Typography, Box, FormControlLabel } from "@mui/material";
import { Checkbox } from "components/Checkbox";

import { TPerson, TTeam, TFunction, THierarchy } from "../../BehaviouralComparison.types";
import DataListStyles from "./DataList.styles";
import { FilterOptionsPill } from "components/FilterOptionsPill";
import { ButtonWithCounter } from "components/Buttons";
import { SnackBar, useSnackBar } from "components/SnackBar";
import { ListOfEntities } from "components/SnackBar/ListOfEntities/ListOfEntities";
import teamReducers from "store/slice/Team";
import { TruncatedText } from "components/TruncatedText";

interface IDataListProps {
    id: string;
    type: string;
    filteredData: TPerson[] | TTeam[] | TFunction[] | THierarchy[];
    handleCheck: (event: React.ChangeEvent<HTMLInputElement>, id: number, type: string) => void;
    pagination?: React.ReactNode;
}

const DataList = ({ id, type, filteredData, handleCheck, pagination }: IDataListProps) => {
    const classes = DataListStyles();

    /*
     IMPORTANT: This dataToRender could change or removed depending on data structure returned by the API.
     Currently, some filters types has a name or label property and others doesn't.
     Also, as seen in other filters, Functions are returned with an object with id: 0 and labeled as ALL (Deafult) which is NOT needed here.
    */
    const dataToRender =
        id === "functions-list" || id === "hierarchies-list"
            ? (filteredData as TFunction[] | THierarchy[]).filter((item) => item.name !== "All (default)")
            : filteredData;

    const isLabelItem = (item: TPerson | TFunction | THierarchy): item is TFunction | THierarchy => {
        return "label" in item;
    };

    const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
    const [usersOnTeam, setUsersOnTeam] = useState<TTeam["users"]>([]);
    const { shouldOpenSnackBar, popOverAnchorEl, handleOpenSnackBar, handleCloseSnackBar } = useSnackBar();

    const handleClickCounter = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>, team: TTeam) => {
        ev.stopPropagation();
        setSelectedTeamId(team.id);
        setUsersOnTeam(team.users);
        handleOpenSnackBar(ev);
    };

    const handleCloseCounter = (event?: React.MouseEvent<HTMLElement>, reason?: "backdropClick" | "escapeKeyDown") => {
        if (event) event.stopPropagation();
        handleCloseSnackBar(event as React.MouseEvent<HTMLElement>, reason);
        setSelectedTeamId(null);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 256 }}>
            <Grid container sx={{ ml: 0, mt: 0 }}>
                {dataToRender.map((item) => (
                    <Grid item lg={6} md={12} xs={6} key={item.id} sx={{ mb: 1.5 }}>
                        <FormControlLabel
                            sx={{ mx: 0 }}
                            label={
                                <>
                                    {id === "people-list" && <Typography sx={{ fontSize: 14 }}>{item.name}</Typography>}

                                    {id === "teams-list" && (
                                        <FilterOptionsPill
                                            filterId="team"
                                            label={
                                                <Box className={classes.pillContainer}>
                                                    <TruncatedText sx={{ fontSize: 12, minWidth: 0, mr: 1 }}>
                                                        {item.name}
                                                    </TruncatedText>
                                                    <ButtonWithCounter
                                                        size={22}
                                                        fontSize={10}
                                                        count={(item as TTeam).count}
                                                        shouldShowIcon={selectedTeamId === item.id}
                                                        colors={["hovered", "info"]}
                                                        callback={(ev) => handleClickCounter(ev, item as TTeam)}
                                                    />
                                                </Box>
                                            }
                                        />
                                    )}

                                    {id === "functions-list" && (
                                        <FilterOptionsPill
                                            filterId="function"
                                            type={isLabelItem(item) ? item.label : undefined}
                                            label={
                                                <Box className={classes.pillContainer}>
                                                    <Typography noWrap fontSize={12}>
                                                        {item.name}
                                                    </Typography>
                                                    <Box className={classes.pillCounter}>3</Box>
                                                </Box>
                                            }
                                        />
                                    )}

                                    {id === "hierarchies-list" && (
                                        <FilterOptionsPill
                                            filterId="hierarchy"
                                            label={
                                                <Box className={classes.pillContainer}>
                                                    <Typography noWrap fontSize={12}>
                                                        {item.name}
                                                    </Typography>
                                                    <Box className={classes.pillCounter}>3</Box>
                                                </Box>
                                            }
                                        />
                                    )}
                                </>
                            }
                            control={
                                <Checkbox
                                    size="small"
                                    className={classes.checkbox}
                                    checked={item.checked}
                                    onChange={(ev) => handleCheck(ev, item.id, type)}
                                />
                            }
                        />
                    </Grid>
                ))}
            </Grid>
            {pagination && pagination}

            <SnackBar
                maxWidth={350}
                shouldOpen={shouldOpenSnackBar}
                anchorElement={popOverAnchorEl}
                handleInfoClose={handleCloseCounter}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                content={
                    <ListOfEntities
                        columnNames={["NAME", "ROLE"]}
                        list={usersOnTeam.map((user) => ({
                            id: user.id,
                            term: user.name,
                            value: user.job_title,
                        }))}
                    />
                }
            />
        </Box>
    );
};

export default DataList;
